import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/**
 * ===========================================================================
 * COMMUNICATIONS
 * ===========================================================================
 * Two things, because only two have somewhere to arrive: a message to a
 * driver, and an email to drivers or customers.
 *
 * WHAT WENT, AND WHY. The old screen configured four automatic notifications —
 * break ending, clock-out due, clock-in missing, pharmacist not arrived. Every
 * one fires off a SCHEDULED time, and the driver app records a clock-in, a
 * clock-out and two odometer readings. There is no schedule to be late
 * against. Those switches could never have fired, and a switch that cannot
 * fire is worse than no switch, because somebody turns it on and stops
 * watching.
 *
 * PUSH IS LIVE, AND IT IS THE ONLY DOORBELL. A message is saved and then
 * pushed to the driver's registered phone through Firebase. The app no longer
 * polls, so a push that does not arrive is a message the driver is not told
 * about until they next open their messages. Two things follow for this
 * screen: it must say what actually happened to each send (delivered, failed
 * and WHY, no phone registered), and it must show up front whether the push
 * chain works at all - credentials, a registered phone, and a validate-only
 * dry run against that phone - before anybody relies on it.
 */

const S = {
  wrap:  { padding: 0, fontFamily: "Inter, system-ui, sans-serif", color: "#111827" },
  h1:    { fontSize: 18, fontWeight: 700, margin: 0 },
  panel: { border: "1px solid #d8dee8", borderRadius: 10, background: "#ffffff", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
           padding: "12px 14px" },
  label: { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
           textTransform: "uppercase", letterSpacing: 0.5 },
  input: { padding: "6px 8px", fontSize: 13, border: "1px solid #d8dee8",
           borderRadius: 8, width: "100%", boxSizing: "border-box",
           fontFamily: "inherit" },
  btn:   { background: "#ffffff", color: "#344054", border: "1px solid #d8dee8",
           borderRadius: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer",
           fontFamily: "inherit" },
  btnGo: { background: "#1976d2", color: "#fff", border: "1px solid #1976d2",
           borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer",
           fontFamily: "inherit", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th:    { textAlign: "left", padding: "5px 7px", borderBottom: "1px solid #e7ebf1",
           fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
           color: "#6b7280" },
  td:    { padding: "5px 7px", borderBottom: "1px solid #f2f4f7" },
  muted: { color: "#6b7280", fontSize: 11 },
  error: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
           padding: 10, borderRadius: 8, fontSize: 13 },
  ok:    { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534",
           padding: 10, borderRadius: 8, fontSize: 13 },
};

async function api(path, options = {}) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE}/api/comms${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Failed (${res.status})`);
  }
  return data;
}

// A second base path, alongside api()'s /api/comms — the outstanding-
// deliveries panel reads from the existing customer register endpoint
// rather than duplicating that query inside comms.service.js.
async function apiCustomers(path, options = {}) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE}/api/customers${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Failed (${res.status})`);
  }
  return data;
}

const when = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "—" : t.toLocaleString("en-GB");
};

export default function Communications({ initialTab } = {}) {
  // Two separate UIs now, not one shared page with a switcher between
  // audiences — reached from two separate dashboard cards / header badges
  // (StaffMessagingCard / CustomerMessagingCard in HeadOffice.js, the two
  // split header badges in App.js), each already telling this component
  // which one via initialTab. No default chooser between the two inside
  // this page any more — falls back to "drivers" only for a stale
  // link/bookmark that predates the split and carries no tab at all.
  const mode = initialTab === "customers" ? "customers" : "drivers";
  const isCustomers = mode === "customers";
  const title = isCustomers ? "Customer messaging" : "Staff messaging";
  const mainLabel = isCustomers ? "Customer messages" : "Staff messages";

  // Just two tabs now, scoped to this one audience: the conversation
  // workspace, and the shared sent-messages log (still unfiltered by
  // audience — it's one combined record of everything sent, by design,
  // same as before the split; only the workspace above it is now
  // audience-specific).
  const [tab, setTab] = useState("main");

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                    marginBottom: 12, flexWrap: "wrap" }}>
        <h1 style={S.h1}>{title}</h1>
        <MaskToggleButton />
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setTab("main")}
            style={{ ...S.btn,
              background: tab === "main" ? "#1976d2" : "#f3f4f6",
              color: tab === "main" ? "#fff" : "#374151",
              borderColor: tab === "main" ? "#1976d2" : "#d1d5db" }}>
            {mainLabel}
          </button>
          <button onClick={() => setTab("log")}
            style={{ ...S.btn,
              background: tab === "log" ? "#1976d2" : "#f3f4f6",
              color: tab === "log" ? "#fff" : "#374151",
              borderColor: tab === "log" ? "#1976d2" : "#d1d5db" }}>
            Messages sent
          </button>
        </div>
      </div>

      {tab === "main" && <MessagingWorkspace mode={mode} />}
      {tab === "log" && <EmailLog />}
    </div>
  );
}

/** Shared multi-select list. Selection is by id, never by index. */
function PickList({ rows, selected, onToggle, render, empty }) {
  if (!rows.length) return <div style={S.muted}>{empty}</div>;
  return (
    <div style={{ maxHeight: "48vh", overflowY: "auto" }}>
      {rows.map((r) => (
        <label key={r.id}
          style={{ display: "flex", gap: 8, alignItems: "flex-start",
                   padding: "5px 6px", borderBottom: "1px solid #f3f4f6",
                   cursor: "pointer" }}>
          <input type="checkbox" checked={selected.has(r.id)}
                 onChange={() => onToggle(r.id)} style={{ marginTop: 3 }} />
          <span style={{ fontSize: 13 }}>{render(r)}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Is push going to work? Asked of the server on every visit and shown at the
 * top of the tab, because the answer decides whether a "Sent" here means a
 * phone buzzed or a row was written.
 *
 * The server checks three things that have each been the real fault: it can
 * mint a Firebase token (key valid), some driver has registered a phone, and
 * a validate-only dry run to that phone is accepted. The last is the only
 * check that catches the app being built against a DIFFERENT Firebase project
 * from the server's key - the credentials are fine, the phone is fine, and
 * every real send fails.
 */
function PushStatus({ status, error, onRetry }) {
  if (error) {
    return (
      <div style={{ ...S.error, marginBottom: 10 }}>
        Could not check push: {error}{" "}
        <button style={S.btn} onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (!status) return <div style={{ ...S.muted, marginBottom: 10 }}>Checking push…</div>;

  const box = (bg, border, color, children) => (
    <div style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 6,
                  padding: "8px 10px", fontSize: 12.5, marginBottom: 10 }}>
      {children}
    </div>
  );
  const devices = `${status.driversWithDevice} of ${status.drivers} driver${status.drivers === 1 ? "" : "s"} `
    + `${status.driversWithDevice === 1 ? "has" : "have"} the app registered`;

  switch (status.verdict) {
    case "live":
      return box("#f0fdf4", "#bbf7d0", "#166534", (
        <>
          <b>Push notifications are live</b> (Firebase project {status.projectId}).{" "}
          {devices}. Dry run to {status.probe.driverName}'s phone accepted.
        </>
      ));
    case "no_devices":
      return box("#fffbeb", "#fde68a", "#92400e", (
        <>
          <b>Push is set up but no phone has registered yet</b> (Firebase project{" "}
          {status.projectId}). {devices}. A driver registers by signing in to the
          app; until then messages are saved and seen when they open them.
        </>
      ));
    case "probe_failed":
      return box("#fef2f2", "#fecaca", "#991b1b", (
        <>
          <b>Push will not reach the phones.</b> The server can talk to Firebase
          (project {status.projectId}) but a dry run to {status.probe.driverName}'s
          phone was refused: {status.probe.error}
          {status.probe.code === "SENDER_ID_MISMATCH" && (
            <div style={{ marginTop: 4 }}>
              Fix: in the Firebase console for the project the APP is built
              against, Project settings → Service accounts → Generate new private
              key, and put its project_id, client_email and private_key into the
              server's FCM_PROJECT_ID, FCM_CLIENT_EMAIL and FCM_PRIVATE_KEY, then
              restart the API.
            </div>
          )}
        </>
      ));
    case "credentials_rejected":
      return box("#fef2f2", "#fecaca", "#991b1b", (
        <>
          <b>Firebase rejected the server's credentials</b>
          {status.projectId ? ` (project ${status.projectId})` : ""}: {status.credentialsError}.
          Messages are still saved; no phone will buzz until this is fixed.
        </>
      ));
    default:
      return box("#f3f4f6", "#d1d5db", "#374151", (
        <>
          <b>Push is not configured on the server.</b> {status.credentialsError}.
          Messages are saved and seen when each driver next opens them in the app.
        </>
      ));
  }
}

/**
 * What happened to a send, in one honest sentence. "Saved for 3" on its own
 * used to read as "3 phones buzzed"; it never meant that.
 */
function SendResult({ result }) {
  const noApp = Math.max(0, result.sent - result.pushable);
  const parts = [];
  if (!result.pushConfigured) {
    parts.push("Push is not configured on the server, so they will see it when they next open their messages in the app.");
  } else {
    if (result.pushDelivered > 0) {
      parts.push(`Pushed to ${result.pushDelivered} phone${result.pushDelivered === 1 ? "" : "s"}.`);
    }
    if (result.pushFailed > 0) {
      parts.push(`${result.pushFailed} push${result.pushFailed === 1 ? "" : "es"} failed.`);
    }
    if (result.tokensRemoved > 0) {
      parts.push(`${result.tokensRemoved} dead registration${result.tokensRemoved === 1 ? "" : "s"} removed - that driver needs to sign in to the app again to be reachable.`);
    }
    if (noApp > 0) {
      parts.push(`${noApp} ${noApp === 1 ? "has" : "have"} no app registered and will see it when they next open their messages.`);
    }
  }
  const bad = result.pushFailed > 0 || result.tokensRemoved > 0;
  return (
    <div style={{ ...(bad ? S.error : S.ok), marginBottom: 10 }}>
      Saved for {result.sent} driver{result.sent === 1 ? "" : "s"}. {parts.join(" ")}
      {result.pushErrors && result.pushErrors.length > 0 && (
        <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
          {result.pushErrors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * MessagingWorkspace — the unified split-panel messaging UI, parameterized
 * for drivers ("staff") or customers. Replaces the old separate
 * MessageDrivers / MessageCustomers implementations: the layout and
 * behaviour asked for is identical for both, only the backend paths and a
 * couple of labels differ.
 *
 * LEFT column, two stacked panels:
 *   Incoming messages    recent activity, most recent first, unread bold
 *                         with a count badge — an inbox, not a directory.
 *   All customers/staff  the full list, searchable, for starting a
 *                         conversation with someone who hasn't messaged in.
 * Clicking a row in EITHER panel opens that person's thread on the right.
 *
 * RIGHT column:
 *   Conversation          every message, oldest first, WhatsApp-style
 *                         bubbles. Same bubble format regardless of
 *                         channel — a small badge on each outgoing message
 *                         shows push or email (and why, on hover, when
 *                         email was a fallback rather than the plan).
 *   Composer               plain text box + Send, directly under the thread.
 *   Outstanding deliveries (customers only) — that person's parcels still
 *                         on the way; hover a row for the full detail via
 *                         the browser's own tooltip, rather than a custom
 *                         floating panel that would need its own
 *                         positioning and click-outside handling.
 */
function MessagingWorkspace({ mode }) {
  const masked = useMaskNames();
  const isCustomers = mode === "customers";
  const idKey = isCustomers ? "recipient_id" : "driver_id";
  const threadsPath = isCustomers ? "/customer_threads" : "/driver_threads";
  const sendPath = isCustomers ? "/customer_messages" : "/messages";
  const peopleLabel = isCustomers ? "customers" : "staff";

  const [threads, setThreads] = useState([]);
  const [threadsError, setThreadsError] = useState(null);
  const [allPeople, setAllPeople] = useState([]);
  const [allError, setAllError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // { id, name }
  const [thread, setThread] = useState(null);
  const [threadError, setThreadError] = useState(null);
  const [content, setContent] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [deliveries, setDeliveries] = useState(null);
  const [deliveriesError, setDeliveriesError] = useState(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api(threadsPath);
      setThreads(r.threads || []);
      setThreadsError(null);
    } catch (e) { setThreadsError(e.message); }
  }, [threadsPath]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const loadAllPeople = useCallback(async () => {
    try {
      if (isCustomers) {
        const q = search.trim()
          ? `?search=${encodeURIComponent(search.trim())}&page_size=50`
          : "?page_size=50";
        const r = await apiCustomers(q);
        setAllPeople((r.customers || []).map((c) => ({
          id: c.id, full_name: c.full_name, email: c.email,
          sub: [c.address, c.postcode].filter(Boolean).join(", "),
        })));
      } else {
        const r = await api("/drivers");
        const term = search.trim().toLowerCase();
        const filtered = term
          ? r.filter((d) => (d.fullName || "").toLowerCase().includes(term))
          : r;
        setAllPeople(filtered.map((d) => ({
          id: d.userId, full_name: d.fullName, email: d.email, sub: d.branchName,
        })));
      }
      setAllError(null);
    } catch (e) { setAllError(e.message); }
  }, [isCustomers, search]);
  useEffect(() => { loadAllPeople(); }, [loadAllPeople]);

  const openThread = async (id, name) => {
    setSelected({ id, name });
    setThreadError(null);
    setContent("");
    setThread(null);
    try {
      const path = isCustomers ? `/customer_threads/${id}` : `/threads/${id}`;
      const r = await api(path);
      setThread(r);
      // Opening it just marked it read server-side — reflect that in the
      // incoming list immediately rather than waiting for the next poll.
      setThreads((rows) => rows.map((t) => (t[idKey] === id ? { ...t, unread: 0 } : t)));
    } catch (e) { setThreadError(e.message); }

    if (isCustomers) {
      setDeliveries(null);
      setDeliveriesError(null);
      try {
        const r = await apiCustomers(`/${id}/items?filter=outstanding`);
        setDeliveries(r.items || []);
      } catch (e) { setDeliveriesError(e.message); }
    }
  };

  const send = async () => {
    if (!selected || !content.trim()) return;
    setSendBusy(true);
    setThreadError(null);
    try {
      const body = isCustomers
        ? { recipientId: selected.id, content: content.trim() }
        : { driverIds: [selected.id], content: content.trim() };
      const r = await api(sendPath, { method: "POST", body: JSON.stringify(body) });
      if (isCustomers) {
        // sendToCustomer returns the refreshed thread directly.
        setThread(r.thread);
      } else {
        // sendToDrivers doesn't (it can message many drivers at once, so a
        // single "the thread" wouldn't make sense there) — reload this one.
        setThread(await api(`/threads/${selected.id}`));
      }
      setContent("");
      loadThreads();
    } catch (e) { setThreadError(e.message); }
    finally { setSendBusy(false); }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      {/* LEFT COLUMN — two stacked panels */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: "0 0 320px" }}>
        <div style={S.panel}>
          <div style={{ ...S.label, marginBottom: 8 }}>Incoming messages</div>
          {threadsError && <div style={S.error}>{threadsError}</div>}
          {!threadsError && threads.length === 0 && <div style={S.muted}>Nothing yet.</div>}
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {threads.map((t) => (
              <ThreadRow key={t[idKey]} t={t} idKey={idKey} isCustomers={isCustomers}
                masked={masked} active={selected?.id === t[idKey]}
                onClick={() => openThread(t[idKey], t.full_name)} />
            ))}
          </div>
        </div>

        <div style={S.panel}>
          <div style={{ ...S.label, marginBottom: 8 }}>All {peopleLabel}</div>
          <input style={{ ...S.input, marginBottom: 8 }} placeholder="Search name or email…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {allError && <div style={S.error}>{allError}</div>}
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {allPeople.map((p) => (
              <div key={p.id} onClick={() => openThread(p.id, p.full_name)}
                style={{
                  padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                  background: selected?.id === p.id ? "#eff6ff" : "transparent",
                }}>
                <div style={{ fontSize: 13 }}>{displayName(p.full_name, masked)}</div>
                {p.sub && <div style={S.muted}>{p.sub}</div>}
              </div>
            ))}
            {allPeople.length === 0 && !allError && <div style={S.muted}>No matches.</div>}
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={S.panel}>
          {!selected ? (
            <div style={S.muted}>Pick someone on the left to see their messages.</div>
          ) : (
            <>
              <div style={{ ...S.label, marginBottom: 8 }}>{displayName(selected.name, masked)}</div>
              {threadError && <div style={{ ...S.error, marginBottom: 8 }}>{threadError}</div>}
              <div style={{ maxHeight: "38vh", overflowY: "auto", marginBottom: 10 }}>
                {thread && thread.messages.length === 0 && (
                  <div style={S.muted}>Nothing sent yet.</div>
                )}
                {thread && thread.messages.map((m) => (
                  <MessageBubble key={m.id} m={m} isCustomers={isCustomers} />
                ))}
                {!thread && !threadError && <div style={S.muted}>Loading…</div>}
              </div>
              <textarea rows={3} style={S.input} value={content} maxLength={2000}
                placeholder="Type a message…" onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }} />
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
                <button style={S.btnGo} disabled={sendBusy || !content.trim()} onClick={send}>
                  {sendBusy ? "Sending…" : "Send"}
                </button>
                <span style={S.muted}>{content.length}/2000</span>
              </div>
            </>
          )}
        </div>

        {isCustomers && selected && (
          <div style={S.panel}>
            <div style={{ ...S.label, marginBottom: 8 }}>Outstanding deliveries</div>
            {deliveriesError && <div style={S.error}>{deliveriesError}</div>}
            {deliveries && deliveries.length === 0 && <div style={S.muted}>Nothing outstanding.</div>}
            {deliveries && deliveries.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {deliveries.map((d) => <DeliveryRow key={d.delivery_id} d={d} />)}
              </div>
            )}
            {!deliveries && !deliveriesError && <div style={S.muted}>Loading…</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadRow({ t, idKey, isCustomers, masked, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: "8px 10px", borderRadius: 6, marginBottom: 4, cursor: "pointer",
      background: active ? "#eff6ff" : "transparent",
      border: `1px solid ${active ? "#93c5fd" : "transparent"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: t.unread > 0 ? 700 : 400, fontSize: 13 }}>
          {displayName(t.full_name, masked)}
        </span>
        {t.unread > 0 && (
          <span style={{
            background: "#dc2626", color: "#fff", borderRadius: 999,
            fontSize: 11, fontWeight: 700, padding: "1px 7px",
          }}>{t.unread}</span>
        )}
      </div>
      {isCustomers && (t.address || t.postcode) && (
        <div style={S.muted}>{[t.address, t.postcode].filter(Boolean).join(", ")}</div>
      )}
      {!isCustomers && t.branch_name && <div style={S.muted}>{t.branch_name}</div>}
      <div style={{ ...S.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {t.last_from_office ? "You: " : ""}{t.last_message} · {when(t.last_at)}
      </div>
    </div>
  );
}

/** Small pill on every outgoing bubble — same format regardless of
 * channel, this is the only thing that differs per message. */
function ChannelBadge({ channel, note }) {
  if (!channel) return null;
  const isPush = channel === "push";
  return (
    <span title={note || (isPush ? "Sent by push notification" : "Sent by email")}
      style={{
        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3,
        padding: "1px 6px", borderRadius: 999,
        background: isPush ? "#dbeafe" : "#fef3c7",
        color: isPush ? "#1e40af" : "#92400e",
        marginLeft: 6,
      }}>
      {isPush ? "Push" : "Email"}
    </span>
  );
}

function MessageBubble({ m, isCustomers }) {
  // The two backends' thread endpoints don't share field-name casing
  // (getThread returns readAt/createdAt, fetchAndMarkThread returns
  // read_at/created_at) — read both rather than pick one and silently
  // break for the other mode.
  const fromOffice = isCustomers ? m.direction === "to_customer" : m.direction === "to_driver";
  const readAt = m.readAt || m.read_at;
  const createdAt = m.createdAt || m.created_at;
  const channelNote = m.channelNote || m.channel_note;

  return (
    <div style={{
      padding: "6px 8px", borderRadius: 6, marginBottom: 5,
      background: fromOffice ? "#eff6ff" : "#f9fafb",
      border: "1px solid #e5e7eb",
    }}>
      <div style={{ fontSize: 13 }}>{m.content}</div>
      <div style={S.muted}>
        {fromOffice ? "Sent" : (isCustomers ? "From customer" : "From driver")} · {when(createdAt)}
        {fromOffice && <ChannelBadge channel={m.channel} note={channelNote} />}
        {fromOffice && (readAt ? ` · read ${when(readAt)}` : " · unread")}
      </div>
    </div>
  );
}

/**
 * One outstanding delivery in the customer panel's bottom list. Hovering
 * reveals the full detail via the browser's native tooltip — deliberately
 * not a custom floating popover, which would need its own positioning and
 * click-outside handling for what the tooltip already does for free.
 */
function DeliveryRow({ d }) {
  const detail = [
    `Status: ${d.status}`,
    d.location_name ? `Location: ${d.location_name}` : null,
    d.eta_minutes != null ? `ETA: ${d.eta_minutes} min` : null,
    d.failure_reason ? `Last attempt: ${d.failure_reason}` : null,
    d.created_at ? `Raised: ${when(d.created_at)}` : null,
  ].filter(Boolean).join("\n");

  return (
    <div title={detail} style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "6px 8px", borderRadius: 6, background: "#f9fafb",
      border: "1px solid #e5e7eb", fontSize: 12, cursor: "default",
    }}>
      <span>
        {d.description || (d.order_type === "collection" ? "Collection" : "Delivery")}
        {d.branch_name ? ` — ${d.branch_name}` : ""}
      </span>
      <span style={S.muted}>{d.status}</span>
    </div>
  );
}

function EmailPanel() {
  const masked = useMaskNames();
  const [audience, setAudience] = useState("drivers");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setSelected(new Set());
    (async () => {
      try {
        if (audience === "drivers") {
          const d = await api("/drivers");
          setRows(d.filter((x) => x.email)
            .map((x) => ({ id: x.userId, name: x.fullName, sub: x.email })));
        } else {
          const q = search ? `?search=${encodeURIComponent(search)}` : "";
          const c = await api(`/customers${q}`);
          setRows(c.map((x) => ({ id: x.id, name: x.fullName, sub: x.email })));
        }
      } catch (e) { setError(e.message); }
    })();
  }, [audience, search]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const send = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api("/email", {
        method: "POST",
        body: JSON.stringify({ audience, ids: [...selected], subject, body }),
      });
      setResult(r);
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ ...S.panel, flex: "0 0 330px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[["drivers", "Drivers"], ["customers", "Customers"]].map(([v, l]) => (
            <button key={v} onClick={() => setAudience(v)}
              style={{ ...S.btn, flex: 1,
                background: audience === v ? "#1976d2" : "#f3f4f6",
                color: audience === v ? "#fff" : "#374151",
                borderColor: audience === v ? "#1976d2" : "#d1d5db" }}>
              {l}
            </button>
          ))}
        </div>

        {audience === "customers" && (
          <input style={{ ...S.input, marginBottom: 8 }} value={search}
            placeholder="Name, email or postcode"
            onChange={(e) => setSearch(e.target.value)} />
        )}

        {/* Only people with an address are listed at all. Showing someone you
            cannot email and failing after the fact wastes the sender's time. */}
        <PickList
          rows={rows} selected={selected} onToggle={toggle}
          empty={audience === "drivers"
            ? "No drivers have an email address on file."
            : "No customers with an email address match."}
          render={(r) => (
            <>
              {displayName(r.name, masked)}
              <div style={S.muted}>{r.sub}</div>
            </>
          )}
        />
      </div>

      <div style={{ ...S.panel, flex: 1, minWidth: 0 }}>
        {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
        {result && (
          <div style={{ ...S.ok, marginBottom: 10 }}>
            {result.sent} sent
            {result.queued > 0 && `, ${result.queued} queued (no mail server configured)`}
            {result.attempted < result.requested && (
              <> · {result.requested - result.attempted} skipped with no address on file</>
            )}
          </div>
        )}

        <div style={S.label}>Subject</div>
        <input style={{ ...S.input, marginTop: 4, marginBottom: 10 }}
          value={subject} maxLength={255}
          onChange={(e) => setSubject(e.target.value)} />

        <div style={S.label}>Message</div>
        <textarea rows={10} style={{ ...S.input, marginTop: 4 }}
          value={body} onChange={(e) => setBody(e.target.value)} />

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <button style={S.btnGo}
            disabled={busy || !subject.trim() || !body.trim() || !selected.size}
            onClick={send}>
            {busy ? "Sending…" : `Send to ${selected.size || "…"}`}
          </button>
          {/* Sent individually, so nobody sees anybody else's address. Worth
              stating, because the alternative is what people assume. */}
          <span style={S.muted}>
            Sent one at a time — recipients never see each other.
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * "Messages sent" — renamed from "Sent email log" and now genuinely
 * covers both channels, reading from GET /api/comms/sent rather than
 * outbound_emails alone. Same ChannelBadge component the conversation
 * bubbles use, for one consistent push/email indicator across the whole
 * page rather than a second design for the log specifically.
 */
function EmailLog() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    api("/sent").then(setRows).catch((e) => setError(e.message));
  }, []);

  if (error) return <div style={S.error}>{error}</div>;
  if (!rows.length) return <div style={{ ...S.panel, ...S.muted }}>Nothing sent yet.</div>;

  const TONE = { sent: "#dcfce7", failed: "#fee2e2", queued: "#fef3c7" };

  return (
    <div style={S.panel}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Channel</th>
            <th style={S.th}>To</th>
            <th style={S.th}>Message</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={S.td}><ChannelBadge channel={r.channel} note={r.note} /></td>
              <td style={S.td}>{r.contact_name || r.to}</td>
              <td style={S.td}>{r.content}</td>
              <td style={{ ...S.td, background: TONE[r.status] || undefined }}>
                {r.status}
                {r.status === "failed" && r.note && <div style={S.muted}>{r.note}</div>}
              </td>
              <td style={S.td}>{when(r.at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
